import * as React from 'react';
import { useEffect, useRef } from 'react';
import { Button, ColumnProps, Justify, Table } from '@rtkwlf/ui';
import Icon from '../../../widgets/Icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  memoizedOnboardingSelectors,
  onboardingSelector,
} from '../../../reducers/onboarding';
import { ONBOARDING_ACTIONS } from '../../../actions/onboarding';
import { useBooleanState } from '../../../hooks';
import { apiRequest } from '../../../APIClient';
import { sessionSelector } from '../../../reducers/session';
import { memoizedHardwareSelectors } from '../../../selectors';
import { ConditionalTooltip } from '../../../widgets/LightTooltip';
import { OnboardingModal } from '../OnboardingModal';
import onboardingUtils from '../../../reducers/onboarding/utils';
import { ROUTE_PATH, SENSOR_CONFIG_LABEL_MAP } from '../../../constants';
import { OBHardwareType, OBSensorDeploymentCapabilities } from '../../../types';
import { HardwareAllotmentInfo } from './OnboardingHardwareAllotmentInfo';
import { useHistory } from 'react-router-dom';
import { useSortTableName } from '../hooks/useSortTableName';
import { getSeriesLabel } from './v2/sensor/constants';
import { useFetchSensorSection } from '../hooks/useFetchSensorSection';
import { ScrollSectionKeys } from './OnboardingSiteConfig';

export const SensorCompleteButton = () => {
  const dispatch = useDispatch();

  const canEdit = useSelector(sessionSelector.canEditOnboarding);
  const completed = useSelector(onboardingSelector.getSensorsCompleted);
  const onCompleteSection = React.useCallback(() => {
    dispatch(ONBOARDING_ACTIONS.TOGGLE_COMPLETE('sensorsSection'));
    dispatch(apiRequest.saveOBSensors());
  }, [dispatch]);

  return (
    <div className="ob-mark-complete-button">
      <Button
        type="primary"
        ghost={completed}
        onClick={onCompleteSection}
        disabled={!canEdit}
        data-cy="sensor-complete-button"
      >
        {completed ? 'Mark Incomplete' : 'Mark Complete'}
      </Button>
    </div>
  );
};

/**
 * a hook to manage the states of the deletion modal
 */
const useSensorDeleteModal = (currentId: string, sensorName: string) => {
  const dispatch = useDispatch();

  const [modalOpen, setModalOpen, openModal, closeModal] =
    useBooleanState(false);

  const onOk = React.useCallback(() => {
    setModalOpen(false);
    dispatch(ONBOARDING_ACTIONS.DELETE_SENSOR(currentId));
    dispatch(apiRequest.saveOBSensors());
  }, [setModalOpen, dispatch, currentId]);

  const modal = (
    <OnboardingModal
      data-cy="sensor-delete-modal"
      title="Delete Sensor"
      visible={modalOpen}
      onCancel={closeModal}
      onOk={onOk}
      okTitle="Yes, delete sensor"
    >
      <p>
        Are you sure you want to delete <b>{sensorName}</b>?
      </p>
    </OnboardingModal>
  );

  return {
    modal,
    openModal,
  };
};

type TableData = {
  key: string;
  name: string;
  site: string;
  model: string;
  series?: string;
  ipAddress: string;
  deploymentType: string;
  finalized: boolean | undefined;
  vanilla: string;
};

const OnboardingSensorList = () => {
  useFetchSensorSection();

  const sensorSectionRef = useRef<null | HTMLDivElement>(null);
  const sensors = useSelector(onboardingSelector.getSensors);
  const sites = useSelector(onboardingSelector.getSites);
  const availableHardware = useSelector(
    memoizedOnboardingSelectors.getAvailableHardware,
  );

  const canEdit = useSelector(sessionSelector.canEditOnboarding);
  const isAwnUser = useSelector(sessionSelector.isAwnUser);

  const genericSeriesEnabled = useSelector(
    onboardingSelector.showGenericSeries,
  );
  const allGenericSensors = useSelector(
    onboardingSelector.getAvailableGenericSensors,
  );

  const history = useHistory();

  useEffect(() => {
    const { location } = history;
    if (location.search === `view=${ScrollSectionKeys.SENSORS}`) {
      setTimeout(() => {
        sensorSectionRef?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'center',
        });
      }, 100);
    }
  }, [sensorSectionRef, history]);

  const onAdd = React.useCallback(
    () => history.push(ROUTE_PATH.ONBOARDING.SENSORS.NEW),
    [history],
  );

  const onEdit = React.useCallback(
    (uuid) => history.push(`${ROUTE_PATH.ONBOARDING.SENSORS.EDIT}/${uuid}`),
    [history],
  );

  const incompleteSensors = useSelector(
    // getIncompleteSensors is a HOF that returns a selector
    memoizedHardwareSelectors.getIncompleteHardware(OBHardwareType.SENSOR),
  );

  const incompleteGenericSeriesSensors = useSelector(
    memoizedHardwareSelectors.getRemainingGenericSeries,
  );

  const remaining = Object.values(
    genericSeriesEnabled ? incompleteGenericSeriesSensors : incompleteSensors,
  ).reduce((a, b) => a + b, 0);
  const canAdd = isAwnUser || remaining > 0;

  const [currentId, setCurrentId] = React.useState('');

  const { modal: deleteModal, openModal: onDelete } = useSensorDeleteModal(
    currentId,
    sensors[currentId]?.name ?? '',
  );

  const editButtonOnClick = React.useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const id = e.currentTarget.dataset.id;
      if (id) {
        onEdit(id);
      }
    },
    [onEdit],
  );
  const deleteButtonOnClick = React.useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const id = e.currentTarget.dataset.id;
      if (id) {
        setCurrentId(id);
        onDelete();
      }
    },
    [setCurrentId, onDelete],
  );
  const { sort, handleSort } = useSortTableName<TableData>();
  const formattedSensors: TableData[] = React.useMemo(() => {
    return sort(
      Object.entries(sensors).map(([id, sensor]) => {
        const {
          name,
          model,
          series,
          ipAddress,
          packetConfig,
          currentCapabilities,
        } = sensor;
        const deploymentCapabilities =
          (currentCapabilities[
            'deployment'
          ] as OBSensorDeploymentCapabilities) ?? '';
        const deploymentType = genericSeriesEnabled
          ? deploymentCapabilities
          : packetConfig;

        const vanilla =
          currentCapabilities['vanilla'] === 'true' ? 'Yes' : 'No';

        return {
          key: id,
          name,
          site: sites[sensor.siteId]?.name ?? '',
          model:
            onboardingUtils.getHardwareInfoByHardwareKey(
              availableHardware.sensors,
              model,
            )?.label ?? model,
          series: getSeriesLabel(allGenericSensors, series ?? ''),
          ipAddress,
          deploymentType: SENSOR_CONFIG_LABEL_MAP[deploymentType],
          finalized: !!sites[sensor.siteId]?.finalized,
          vanilla,
        };
      }),
    );
  }, [
    availableHardware.sensors,
    sites,
    sensors,
    sort,
    allGenericSensors,
    genericSeriesEnabled,
  ]);

  const columns: ColumnProps<TableData>[] = React.useMemo(() => {
    const result: ColumnProps<TableData>[] = [
      {
        key: 'name',
        dataIndex: 'name',
        title: 'Name',
        sortable: true,
      },
      {
        key: 'site',
        dataIndex: 'site',
        title: 'Site',
      },
      {
        key: 'ipAddress',
        dataIndex: 'ipAddress',
        title: 'IP Address',
      },
      {
        key: 'deploymentType',
        dataIndex: 'deploymentType',
        title: 'Deployment Type',
      },
    ];
    if (genericSeriesEnabled) {
      result.splice(
        2,
        0,
        {
          key: 'series',
          dataIndex: 'series',
          title: 'Series',
        },
        {
          key: 'vanilla',
          dataIndex: 'vanilla',
          title: 'Customer Configured',
        },
      );
      if (isAwnUser) {
        result.splice(3, 0, {
          key: 'model',
          dataIndex: 'model',
          title: 'Model',
        });
      }
    } else {
      result.splice(2, 0, {
        key: 'model',
        dataIndex: 'model',
        title: 'Model',
      });
    }

    if (canEdit) {
      result.push({
        key: 'actions',
        title: 'Actions',
        width: 5,
        render: ({ record: { finalized, key } }) => {
          if (!finalized) {
            return (
              <>
                <Icon.Pencil
                  data-cy={`edit-sensor-${key}`}
                  data-id={key}
                  className="mr-2"
                  style={{ cursor: 'pointer' }}
                  onClick={editButtonOnClick}
                />
                <Icon.X
                  data-cy={`delete-sensor-${key}`}
                  data-id={key}
                  style={{ cursor: 'pointer' }}
                  onClick={deleteButtonOnClick}
                />
              </>
            );
          } else {
            return <></>;
          }
        },
        justify: 'flex-end' as Justify,
      });
    }
    return result;
  }, [
    canEdit,
    editButtonOnClick,
    deleteButtonOnClick,
    genericSeriesEnabled,
    isAwnUser,
  ]);

  return (
    <>
      <HardwareAllotmentInfo type={OBHardwareType.SENSOR} />

      {canEdit && (
        <div
          className="d-flex flex-row-reverse"
          data-cy="add-sensor-div"
          ref={sensorSectionRef}
        >
          <ConditionalTooltip
            visible={!canAdd}
            tooltip="Contact your Onboarding team to purchase additional sensors."
          >
            <Button
              type="primary"
              ghost
              className="mb-2"
              onClick={onAdd}
              disabled={!canAdd}
              data-cy="add-sensor-button"
            >
              <Icon.Plus className="mr-2" />
              Configure Sensor
            </Button>
          </ConditionalTooltip>
        </div>
      )}

      <Table
        data={formattedSensors}
        dataUniqueKey="value"
        columns={columns}
        onSort={handleSort}
        emptyComponent={
          canEdit &&
          canAdd && (
            <div
              className="my-5 text-center"
              data-cy="add-sensor-table-placeholder"
            >
              <span>
                Select
                <Button
                  type="link"
                  className="d-inline px-1 font-normal"
                  onClick={onAdd}
                >
                  Configure Sensor
                </Button>
                to configure an included sensor
              </span>
            </div>
          )
        }
      />

      {deleteModal}
    </>
  );
};

export default OnboardingSensorList;
